<template>
<div class="chapter">
<div class="inner_page">
<p class="ct" id="pz9-1">
<a href="#/contents#preface">PREFACE</a> </p>
<p class="tx" style="margin-top:3em;">
					The account which follows embraces three kinds of Russian occasions, pretty much
					in chronological order. In 1961-2, I spent a year in Moscow as a graduate student,
					working on my dissertation. Between 1968 and 1997, I accompanied six groups of students
					and one group of alumni on tours of European Russia. In 1991 and 1997 I directed
					semester programs for the Associated Colleges of the Midwest-Great Lakes College
					Association in the south Russian city of Krasnodar. In between, I taught Russian
					literature at Lawrence University in Appleton, Wisconsin. Herein, I attempt to give
					form to my experience. </p>
<p class="tx">
					I believe that I have something to add to the ongoing discussion, in the Western
					press as in every Russian kitchen, of the two perennial questions: “Who is to blame?”
					and “What is to be done?” Both are book titles and the major questions regarding
					Russia’s fate. My mite, however, in the sea of Russia discussion, does not pretend
					to <i>answer</i> these questions, but is rather an effort to describe the tea-and-vodka-ringed
					pine table over which these questions are raised: private Russia. As my Russian is
					decent, though not native, and my experience lengthy, I hope to be able to interest
					you mightily in this strange place which I have learned to love. </p>
<p class="tx">
					I do not intend to deal mainly with political matters as such. I will try, <span id="z10-1">rather,
					to show what the Russia of my experience, between 1961 and 1997, has been able to
					teach me about a way of life quite foreign to, but not always less attractive than,
					the “certain certainties” of American and European culture. </span></p>
<p class="tx">
					When I return from Russia, ten times now since 1961, most people are utterly incurious
					about the place. “Why do you go there?” is the most frequent query. When I explain
					that I teach Russian literature, my reason seems sufficient, if esoteric. No more
					questions. There were two “questioners” at our basement apartment door three months
					after my first wife, Lois, and I returned in 1962. These FBI men queried, “Were you
					asked to be spies?” Then they warned: “Let us know if you are approached.” They gave
					us a phone number. </p>
<p class="tx">
					That encounter was telling. Even the FBI was, essentially, incurious. They “knew,”
					to the point of boredom, what Ronald Reagan thought when twenty-five years later
					he called the Soviet Union an “evil empire.” Who would <i>want</i> to be a Russian spy?
					Hadn’t the media made it clear that Russia was the most wretched, ghastly place on
					earth? Everyone <i>knew</i> all about Russia. </p>
<p class="tx">
					If the FBI had been interested, I could have given reasons for my interest in Russia.
					In an unhappy family, I read a lot: Turgenev, Dostoevsky, and Tolstoy included. In
					college one redhead and two films, <i>The Red Shoes</i> and <i>The Tales of Hoffmann</i> moved
					me from pursuing moneymaking engineering to the less promising humanities. German
					and Polish roots started me on languages. Labor union and Hungarian socialist friends
					pointed me east. What I really wanted was to belong to that warm, thoughtful world
					that <span id="z11-1">Turgenev seemed to represent, to escape the banal, dollar boosterism which,
					as I thought then, was all that America had to offer. </span></p>
<p class="tx">
					Now, fifty years later, I think that Russia was attractive to me because the characters
					of Turgenev, Tolstoy, and Dostoevsky were not exotic, but rather very close to the
					subsistence farmers who largely raised me. The excuses of sociology for human faults
					were simply beyond the pale of my models. Belief in taking responsibility for oneself
					in a difficult life was so prevalent in Genesee village that no one needed to brood
					upon it. When I chose Russia as my subject matter and as my career it was, I guess,
					because Russia was the closest thing to Genesee village that I had ever come upon,
					not because of my green Marxism. </p>
<p class="tx">
					My left-wing youth has mellowed, much too much, into middle-class comfort. Neither
					have I escaped the occasional foray into sociological excuse making. Still, for fifty
					years, private Russia has provided the generosity, idealism, and warmth which I hoped
					to find in her, as I found it on an eleven cow dairy farm in Wisconsin in the thirties.
					Perhaps, here, I will be able to convince you of this. </p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// preface.vue?185349fe